import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  isEmptyDataEducationWithFilters,
  removeCurrentEducation,
  setCurrentEducation,
  setLoadingEducation,
} from '@pages/hubs/Education/store/education-slice';
import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';
import i18next from '@setup/locales/i18n';
import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';
import { StatusServerErrors } from '@shared/enums/status-server-errors';

import type { EducationType } from '@pages/hubs/Education/store/education-types';
import type { RootMeta } from '@pages/hubs/Garage/store/types';
import type { GetHubsParams } from '@pages/hubs/Pets/store/pets-types';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';
import type { EntityHubSocket } from '@shared/models/hubs/hub-socket-response';
import type { AppDispatch, RootState } from '@store/store';

export const getEducations = createAsyncThunk<
  { data: EducationType[]; meta: RootMeta },
  Partial<GetHubsParams>,
  { rejectValue: ErrorType; dispatch: AppDispatch; state: RootState }
>('getEducations', async (educationParams, { dispatch, rejectWithValue }) => {
  dispatch(setLoadingEducation({ isLoading: false }));
  try {
    const { data, meta } = await api.education.getEducations({
      page: educationParams.page || 1,
      ...educationParams,
    });
    dispatch(isEmptyDataEducationWithFilters({ filters: educationParams, educationData: data }));

    return {
      data,
      meta,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  } finally {
    dispatch(setLoadingEducation({ isLoading: true }));
  }
});

export const getEducation = createAsyncThunk<EducationType, number, { rejectValue: ErrorType }>(
  'getEducation',
  async (educationID, { rejectWithValue, dispatch }) => {
    try {
      const education = await api.education.getEducation(educationID);
      dispatch(setCurrentEducation({ education }));
      return education;
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(
        e,
        {
          [StatusServerErrors.AccessDenied]: {
            message: i18next.t('general.notifications.educationNotSharedWithYou'),
          },
          [StatusServerErrors.ItemNotFound]: {
            message: i18next.t('general.notifications.educationNotFound'),
          },
        },
        {
          notificationVariant: 'info',
        },
      );

      return rejectWithValue(axiosError.data);
    }
  },
);

export const createEducation = createAsyncThunk<
  EducationType,
  Partial<EducationType>,
  { rejectValue: ErrorType }
>('createEducation', async (data, { rejectWithValue }) => {
  try {
    return await api.education.createEducation(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });

    return rejectWithValue(axiosError.data);
  }
});

export const updateEducation = createAsyncThunk<
  EducationType,
  { data: Partial<Omit<EducationType, 'contacts'> & { contacts: { id: number }[] }>; educationID: number },
  { rejectValue: ErrorType }
>('updateEducation', async ({ data, educationID }, { rejectWithValue }) => {
  try {
    return await api.education.updateEducation(data, educationID);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });

    return rejectWithValue(axiosError.data);
  }
});

export const removeEducation = createAsyncThunk<any, { educationID: number }, { rejectValue: ErrorType }>(
  'removeEducation',
  async ({ educationID }, { rejectWithValue, dispatch }) => {
    try {
      return await api.education.removeEducation(educationID).then(() => {
        dispatch(removeCurrentEducation({ educationID }));
        NotificationService.success(i18next.t('education.notifications.removeEducation'));
      });
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const getPlaceAutocomplete = createAsyncThunk<
  any,
  { filterType: string | undefined; valueAutocomplete: string },
  { rejectValue: ErrorType }
>('getPlaceAutocomplete', async (data, { rejectWithValue }) => {
  try {
    const responseAutocomplete = await api.education.placeAutocomplete(data);

    return responseAutocomplete.map((el) => el.description);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });

    return rejectWithValue(axiosError.data);
  }
});

// ---------------- web-socket ----------------

export const updateEducationWS = createAsyncThunk<
  {
    educationWS: EducationType;
  },
  EntityHubSocket,
  { rejectValue: ErrorType }
>('updateEducationWS', async (socketEvent, { rejectWithValue }) => {
  try {
    const updateEducationResponse = await api.education.getEducation(socketEvent.entity_id);

    return {
      educationWS: updateEducationResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const addEducationWS = createAsyncThunk<
  {
    addEducationWS: EducationType;
  },
  EntityHubSocket,
  { rejectValue: ErrorType }
>('addEducationWS', async (socketEvent, { rejectWithValue }) => {
  try {
    const addEducationResponse = await api.education.getEducation(socketEvent.entity_id);

    return {
      addEducationWS: addEducationResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const acceptedEducationWS = createAsyncThunk<
  {
    acceptedEducationWS: EducationType;
  },
  number,
  { rejectValue: ErrorType }
>('acceptedEducationWS', async (educationID, { rejectWithValue }) => {
  try {
    const acceptedEducationResponse = await api.education.getEducation(educationID);

    return {
      acceptedEducationWS: acceptedEducationResponse,
    };
  } catch (e: any) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
